import {
  styled,
} from "@mui/material";

import SelectField from "controls/global/select-field";
import {
  useSignatureDispatchProvider,
  useSignatureProvider,
} from "pages/signatures/providers/manage-signature/SignatureProvider";
import React, {
  useEffect,
  useState,
} from "react";
import {
  colors,
  fontSize,
  fontWeight,
  margin,
} from "theme/defaultStyle";
import { useLookup } from "utils/context/LookupContext";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { SignatureLevelType } from "utils/data/enum";
import SignatureConfirmationDialog from "../signature-confirmation-dialog/SignatureConfirmationDialog";

interface SelectOption {
  value: string;
  text: string;
}

const FormField = styled("div")({
  marginTop: margin.xlarge,
  "& .label": {
    color: colors.grey08,
    fontSize: fontSize.large,
    fontWeight: fontWeight.normal1,
    marginBottom: margin.small,
  },
});

const fieldName = "signatureLevelTypeCode";

const UPDATE_SIGNATURE_TITLE = "Update this signature";
const UPDATE_DEFAULTED_SIGNATURES_MESSAGE =
  "This signature is defaulted for one or more users.";

const LevelField = ({ disabledField }: { disabledField?: () => boolean }) => {
  const { setValue, watch } = useFormWrapper();
  const {
    state: { signature},
  } = useSignatureProvider();
  const { dispatch } = useSignatureDispatchProvider();

  const [levelOptions, setLevelOptions] = useState<SelectOption[]>([]);
  const [showEditWarningDialog, setShowEditWarningDialog] = useState(false);
  const [{ signatureLevelTypes }] = useLookup();


  // Watch the current form value
  const currentValue = watch(fieldName);


  const handleOnChange = (event: React.ChangeEvent<any>) => {
    const signatureLevelTypeCode = event.target.value;
    if (
      signatureLevelTypeCode === SignatureLevelType.UserOnly &&
      signature.isDefaultedByOther === 1
    ) {
      dispatch({
        type: "LEVEL_UPDATE",
        signatureLevelTypeCode: signatureLevelTypeCode,
      });
      setShowEditWarningDialog(true);
    } else {
      dispatch({
        type: "LEVEL_UPDATE",
        signatureLevelTypeCode: signatureLevelTypeCode,
      });
    }
  };

  const handleContinueEdit = () => {
    setShowEditWarningDialog(false);
  };

  const handleCancelEdit = () => {
    setValue(fieldName, {
      value: signature.signatureLevelTypeCode || SignatureLevelType.AgencyWide,
    });
    setShowEditWarningDialog(false);
  };

  useEffect(() => {
    setLevelOptions(
      signatureLevelTypes.map((t) => ({
        value: t.code,
        text: t.name,
      }))
    );
  }, [signatureLevelTypes]);

  // Update form when signature level changes, but only if form value is different
  useEffect(() => {
    const formValue = currentValue?.value;
    const signatureValue = signature.signatureLevelTypeCode || SignatureLevelType.AgencyWide;

    // Only update if values are actually different to prevent loops
    if (formValue !== signatureValue) {
      setValue(fieldName, {
        value: signatureValue,
      });
    }
  }, [signature.signatureLevelTypeCode, currentValue, setValue]);

  return (
    <FormField>
      <SelectField
        label="Signature Access"
        name={fieldName}
        options={levelOptions}
        optionsValueField="value"
        optionsTextField="text"
        dataValueField="value"
        dataTextField="text"
        disabled={disabledField ? disabledField() : false}
        onChange={handleOnChange}
      />
      <SignatureConfirmationDialog
        width={530}
        isOpen={showEditWarningDialog}
        title={UPDATE_SIGNATURE_TITLE}
        subtitle={UPDATE_DEFAULTED_SIGNATURES_MESSAGE}
        iconType="info"
        onPrimaryAction={handleContinueEdit}
        primaryButtonText={"Continue"}
        primaryButtonColor="primary"
        disablePrimaryButton={false}
        onSecondaryAction={handleCancelEdit}
        secondaryButtonText={"Cancel"}
        disableSecondaryButton={false}
        showCloseIcon={true}
        onClose={handleCancelEdit}
      />
    </FormField>
  );
};

export default LevelField;
